import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getCookie, removeCookie } from "@/app/api/jwt/fetcher";
import { AxiosError } from "axios";
import { trackUser } from "@/_components/lib/mixpanel/mixpanelClient";
import { authService, IUser } from "@/_services/userServices/auth";

interface IAuthState {
  token: string;
  userId: string;
  expiredAt: Date | null;
  user: IUser | null;
  roleId: string;
  isLoading: boolean;
  setAuth(
    token: string,
    userId: string,
    expiredAt: string,
    roleId: string,
  ): void;
  setUser(user: IUser | null): void;
  setLoading(isLoading: boolean): void;
  removeAuth(): void;
  getAuth(): { token: string; userId: string } | null;
  initializeFromCookie(force?: boolean): Promise<void>;
}

export const useAuthStore = create<IAuthState>()(
  devtools((set, get) => ({
    token: "",
    userId: "",
    expiredAt: null,
    roleId: "",
    user: null,
    isLoading: true,
    setAuth: (token, userId, expiredAt, roleId) =>
      set(
        {
          token,
          userId,
          expiredAt: new Date(expiredAt),
          roleId,
        },
        undefined,
        "authStore/setAuth",
      ),
    setUser: (user) => set({ user }, undefined, "authStore/setUser"),
    setLoading: (isLoading) =>
      set({ isLoading }, undefined, "authStore/setLoading"),
    removeAuth: () =>
      set(
        {
          token: "",
          userId: "",
          expiredAt: null,
          roleId: "",
          user: null,
        },
        undefined,
        "authStore/removeAuth",
      ),
    getAuth: () => {
      const state = get();
      if (!state.token || !state.userId || !state.expiredAt) {
        return null;
      }
      const now = new Date();
      if (now > state.expiredAt) {
        return null;
      }
      return {
        token: state.token,
        userId: state.userId,
        roleId: state.roleId,
      };
    },
    initializeFromCookie: async (force = false) => {
      const { token, userId, expiredAt, user } = get();
      const fetchAndSetUser = async () => {
        try {
          if (user) {
            return;
          }
          const userData = await authService.getCurrentUser();
          if (userData) {
            trackUser(userData as IUser);
            set({ user: userData as IUser }, undefined, "authStore/setUser");
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error);
        }
      };

      try {
        if (token && user && !force) {
          console.log("Already initialized, SKIP");
          set({ isLoading: false });
          return;
        }

        set({ isLoading: true }, undefined, "authStore/setLoading");

        const hasAuthData = Boolean(token && userId && expiredAt);

        if (hasAuthData) {
          await fetchAndSetUser();
          set({ isLoading: false }, undefined, "authStore/setLoading");
          return;
        }

        const res = await getCookie();

        // get Cookie failed
        if (!res || res.status !== 200) {
          set({ isLoading: false }, undefined, "authStore/setLoading");
          return;
        }

        const data = res.data;
        // In complete data
        if (!data?.token || !data?.userId || !data?.expiredAt) {
          set({ isLoading: false }, undefined, "authStore/setLoading");
          return;
        }

        // Token expired
        if (new Date(data.expiredAt) < new Date()) {
          await removeCookie();
          set({ isLoading: false }, undefined, "authStore/setLoading");
          return;
        }

        if (process.env.NEXT_PUBLIC_ENV !== "prod") {
          console.log("data", data);
        }

        set({
          token: data.token,
          userId: data.userId,
          expiredAt: new Date(data.expiredAt),
          roleId: data.roleId,
        });

        await fetchAndSetUser();
      } catch (error) {
        console.error("Failed to initialize auth from cookie:", error);
        if ((error as AxiosError).status === 403) {
          throw new Error("Authentication error 403");
        }
      } finally {
        set({ isLoading: false });
      }
    },
  })),
);
